import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';
import { linkOptions } from '@tanstack/react-router';
import { Badge } from '@anyui/core';
import { formatIntNumber, formatPercent, formatPrice } from '@anytag/shared-utils';
import { Anchor, CheckBox, TableCell, TextCutter, ColumnCustomiser } from '@anytag/shared-atoms';
import {
  AllTiktokAdReportSummary,
  TiktokAdDetailReport,
  TikTokAdReportOrderField,
  TikTokAdReportType,
  TikTokAdStatus
} from '@anytag/graphql';
import { useToggleTableColumns } from '@anytag/shared-hooks';
import { getAdReportStatusColumnProps } from '../utils';
import { AdCampaignReportRowData, AdCampaignReportColumnKeys } from '../types';

export interface AdCampaignReportTableProps {
  loading?: boolean;
  adCampaignIdFromUrl?: string;
  selectedAdCampaignIds: string[];
  adReportType: TikTokAdReportType;
  summaryData?: AllTiktokAdReportSummary | null;
  onClickCampaignId: (id: string | string[]) => void;
  allTiktokAdsReport: readonly TiktokAdDetailReport[];
}

export const useAdCampaignReportTable = ({
  summaryData,
  adReportType,
  onClickCampaignId,
  adCampaignIdFromUrl,
  selectedAdCampaignIds
}: AdCampaignReportTableProps) => {
  const { t } = useTranslation();
  const { getSelectableColumns, handleToggleColumns } = useToggleTableColumns<
    AdCampaignReportRowData,
    AdCampaignReportColumnKeys
  >();

  const total = summaryData?.total ?? 0;
  const currency = t(summaryData?.currency || '');

  const { title, trailingKey } = (() => {
    switch (adReportType) {
      case TikTokAdReportType.AD_CAMPAIGN:
        return { title: 'Campaign', trailingKey: 'campaign' };
      case TikTokAdReportType.AD_GROUP:
        return { title: 'Ad Group', trailingKey: 'group' };
      case TikTokAdReportType.AD:
        return { title: 'Ad', trailingKey: 'ad' };
      default:
        return { title: '', trailingKey: '' };
    }
  })();

  // based on https://adasiaholdings.atlassian.net/wiki/spaces/NC/pages/3567157291/Ad+Report#The-metrics-we-wanna-show
  const columns: ColumnDef<AdCampaignReportRowData>[] = [
    {
      enableHiding: false,
      accessorKey: 'title',
      meta: { sticky: true },
      header: ({ table }) => {
        const { selectableOptions } = getSelectableColumns(table);

        return (
          <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {t(`HeaderColumn.${title}`)}
            <ColumnCustomiser
              options={selectableOptions}
              onChange={handleToggleColumns(table)}
              savedValuesKey="adReportTableColumns"
              buttonProps={{
                tooltip: { place: 'top', help: <span className="text-sm">{t('Button.Customise report table')}</span> }
              }}
            />
          </div>
        );
      },
      cell: ({ row }) => {
        const { className, adCampaignInfo } = row.original;

        if (className === 'total-row') return <TableCell.CampaignTotal count={total ?? 0} />;

        if (!adCampaignInfo) return null;

        const { status, adId, adName, adGroupId, adGroupName, adCampaignId, adCampaignName, campaignId, postId } =
          adCampaignInfo;
        let nextLevelLink;
        let itemId;
        let itemName;

        switch (adReportType) {
          case TikTokAdReportType.AD:
            itemId = adId;
            itemName = adName;
            nextLevelLink = linkOptions({
              to: '/tiktok/campaigns/$id/posts/$postId',
              params: { id: campaignId?.toString() || '', postId: postId?.toString() || '' }
            });
            break;
          case TikTokAdReportType.AD_GROUP:
            itemId = adGroupId;
            itemName = adGroupName;
            nextLevelLink = linkOptions({
              to: '/tiktok/ad-campaign-report/$id/$groupId',
              params: { id: adCampaignIdFromUrl || '', groupId: adGroupId || '' }
            });
            break;

          case TikTokAdReportType.AD_CAMPAIGN:
          default:
            itemId = adCampaignId;
            itemName = adCampaignName;
            nextLevelLink = linkOptions({ to: '/tiktok/ad-campaign-report/$id', params: { id: adCampaignId || '' } });
        }

        return (
          <div css={{ display: 'flex', gap: '8px' }}>
            <CheckBox
              checked={itemId ? selectedAdCampaignIds.includes(itemId) : false}
              onClick={() => (itemId ? onClickCampaignId(itemId) : null)}
            />
            <Anchor
              {...nextLevelLink}
              disabled={adReportType === TikTokAdReportType.AD && status === TikTokAdStatus.DISABLE}
            >
              <TextCutter text={itemName} />
            </Anchor>
          </div>
        );
      }
    },
    {
      accessorKey: 'status',
      header: t<string>('HeaderColumn.Status'),
      meta: {
        sortField: TikTokAdReportOrderField.AD_STATUS,
        help: t<string>(`Tooltip.adStatusHelp_${trailingKey}`)
      },
      cell: ({ row }) =>
        row.original.adCampaignInfo?.status === null ? (
          '-'
        ) : (
          <Badge {...getAdReportStatusColumnProps(row.original.adCampaignInfo?.status, t)} />
        )
    },
    {
      accessorKey: 'adBudget',
      header: t<string>('HeaderColumn.Budget'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_BUDGET,
        help: t<string>(`Tooltip.adBudgetHelp_${trailingKey}`)
      },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats?.adBudget ? currency : ''}
          value={formatPrice(row.original.stats?.adBudget || null, currency)}
        />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'adCost',
      header: t<string>('HeaderColumn.Total Cost'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_COST,
        help: t<string>('Tooltip.adCostHelp')
      },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats?.adCost ? currency : ''}
          value={formatPrice(row.original.stats?.adCost || null, currency)}
        />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'adImpressions',
      header: t<string>('HeaderColumn.Ad Impressions'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_IMPRESSIONS,
        help: t<string>('Tooltip.adImpressionsHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adImpressions)} />
    },
    {
      enableHiding: false,
      accessorKey: 'adCpm',
      header: t<string>('HeaderColumn.CPM'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_CPM,
        help: t<string>('Tooltip.adCpmHelp')
      },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats?.adCpm ? currency : ''}
          value={formatPrice(row.original.stats?.adCpm || null, currency)}
        />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'adReach',
      header: t<string>('HeaderColumn.Ad Reach'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_REACH,
        help: t<string>('Tooltip.adReachHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adReach)} />
    },
    {
      enableHiding: false,
      accessorKey: 'adFrequency',
      header: t<string>('HeaderColumn.Frequency'),
      meta: {
        sortField: TikTokAdReportOrderField.AD_FREQUENCY,
        help: t<string>('Tooltip.adFrequencyHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adFrequency)} />
    },
    {
      enableHiding: false,
      accessorKey: 'adClicks',
      header: t<string>('HeaderColumn.Clicks (destination)'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_CLICKS,
        help: t<string>('Tooltip.adClicksHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adClicks)} />
    },
    {
      enableHiding: false,
      accessorKey: 'adCpc',
      header: t<string>('HeaderColumn.CPC (destination)'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_CPC,
        help: t<string>('Tooltip.adCpcHelp')
      },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats?.adCpc ? currency : ''}
          value={formatPrice(row.original.stats?.adCpc || null, currency)}
        />
      )
    },
    {
      enableHiding: false,
      accessorKey: 'adCtr',
      header: t<string>('HeaderColumn.CTR (destination)'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_CTR,
        help: t<string>('Tooltip.adCtrHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.adCtr, false)} unit="%" />
    },
    {
      accessorKey: 'adResult',
      header: t<string>('HeaderColumn.Results'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_RESULT,
        help: t<string>('Tooltip.adResultHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adResult)} />
    },
    {
      accessorKey: 'adCostPerResult',
      header: t<string>('HeaderColumn.Cost Per Result'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_COST_PER_RESULT,
        help: t<string>('Tooltip.adCostPerResultHelp')
      },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats?.adCostPerResult ? currency : ''}
          value={formatPrice(row.original.stats?.adCostPerResult || null, currency)}
        />
      )
    },
    {
      accessorKey: 'adCostPer1000Reached',
      header: t<string>('HeaderColumn.Cost per 1000 people reached'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_COST_PER_1000_REACHED,
        help: t<string>('Tooltip.adCostPer1000ReachedHelp')
      },
      cell: ({ row }) => (
        <TableCell.Number
          unit={row.original.stats?.adCostPer1000Reached ? currency : ''}
          value={formatPrice(row.original.stats?.adCostPer1000Reached || null, currency)}
        />
      )
    },
    {
      accessorKey: 'adResultRate',
      header: t<string>('HeaderColumn.Result Rate'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_RESULT_RATE,
        help: t<string>('Tooltip.aadResultRateHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.adResultRate, false)} unit="%" />
    },
    {
      enableHiding: false,
      accessorKey: 'videoPlayActions',
      header: t<string>('HeaderColumn.Video View'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.VIDEO_PLAY_ACTIONS,
        help: t<string>('Tooltip.videoPlayActionsHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.videoPlayActions)} />
    },
    {
      enableHiding: false,
      accessorKey: 'videoWatched2s',
      header: t<string>('HeaderColumn.2-second video views'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.VIDEO_WATCHED_2S,
        help: t<string>('Tooltip.videoWatched2sHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.videoWatched2s)} />
    },
    {
      enableHiding: false,
      accessorKey: 'videoWatched6s',
      header: t<string>('HeaderColumn.6-second video views'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.VIDEO_WATCHED_6S,
        help: t<string>('Tooltip.videoWatched6sHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.videoWatched6s)} />
    },
    {
      accessorKey: 'videoViewsP25',
      header: t<string>('HeaderColumn.Video views at 25%'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.VIDEO_VIEWS_P25,
        help: t<string>('Tooltip.videoViewsP25Help')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.videoViewsP25)} />
    },
    {
      accessorKey: 'videoViewsP50',
      header: t<string>('HeaderColumn.Video views at 50%'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.VIDEO_VIEWS_P50,
        help: t<string>('Tooltip.videoViewsP50Help')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.videoViewsP50)} />
    },
    {
      accessorKey: 'videoViewsP75',
      header: t<string>('HeaderColumn.Video views at 75%'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.VIDEO_VIEWS_P75,
        help: t<string>('Tooltip.videoViewsP75Help')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.videoViewsP75)} />
    },
    {
      accessorKey: 'videoViewsP100',
      header: t<string>('HeaderColumn.Video views at 100%'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.VIDEO_VIEWS_P100,
        help: t<string>('Tooltip.videoViewsP100Help')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.videoViewsP100)} />
    },
    {
      accessorKey: 'averageVideoPlayPerUser',
      header: t<string>('HeaderColumn.Average play time per user'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AVERAGE_VIDEO_PLAY_PER_USER,
        help: t<string>('Tooltip.averageVideoPlayPerUserHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.averageVideoPlayPerUser)} />
    },
    {
      accessorKey: 'averageVideoPlay',
      header: t<string>('HeaderColumn.Average play time per video view'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AVERAGE_VIDEO_PLAY,
        help: t<string>('Tooltip.averageVideoPlayHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.averageVideoPlay)} />
    },
    {
      accessorKey: 'adEngagements',
      header: t<string>('HeaderColumn.Clicks (all)'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_ENGAGEMENTS,
        help: t<string>('Tooltip.adEngagementsHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adEngagements)} />
    },
    {
      accessorKey: 'adEr',
      header: t<string>('HeaderColumn.CTR (all)'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_ER,
        help: t<string>('Tooltip.adErHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.stats?.adEr, false)} unit="%" />
    },
    {
      accessorKey: 'adFollows',
      header: t<string>('HeaderColumn.Paid Follows'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_FOLLOWS,
        help: t<string>('Tooltip.adFollowsHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adFollows)} />
    },
    {
      accessorKey: 'adLikes',
      header: t<string>('HeaderColumn.Paid Likes'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_LIKES,
        help: t<string>('Tooltip.adLikesHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adLikes)} />
    },
    {
      accessorKey: 'adComments',
      header: t<string>('HeaderColumn.Paid Comments'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_COMMENTS,
        help: t<string>('Tooltip.adCommentsHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adComments)} />
    },
    {
      accessorKey: 'adShares',
      header: t<string>('HeaderColumn.Paid Shares'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.AD_SHARES,
        help: t<string>('Tooltip.adSharesHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.adShares)} />
    },
    {
      accessorKey: 'interactiveAddOnImpressions',
      header: t<string>('HeaderColumn.Interactive add-on impressions'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.INTERACTIVE_ADD_ON_IMPRESSIONS,
        help: t<string>('Tooltip.interactiveAddOnImpressionsHelp')
      },
      cell: ({ row }) => <TableCell.Number value={formatIntNumber(row.original.stats?.interactiveAddOnImpressions)} />
    },
    {
      accessorKey: 'interactiveAddOnDestinationClicks',
      header: t<string>('HeaderColumn.Interactive add-on destination clicks'),
      meta: {
        align: 'right',
        sortField: TikTokAdReportOrderField.INTERACTIVE_ADD_ON_DESTINATION_CLICKS,
        help: t<string>('Tooltip.interactiveAddOnDestinationClicksHelp')
      },
      cell: ({ row }) => (
        <TableCell.Number value={formatIntNumber(row.original.stats?.interactiveAddOnDestinationClicks)} />
      )
    }
  ];

  return { columns };
};
